/**
 * Shape helpers for the item and boss models, on top of `MeshBuilder`: a triangle collector that
 * writes auto-smoothed normals (curves shade as cel bands, creases above the crease angle stay
 * sharp), and the lathe, tube, grid and bevelled slab built on it. Local frame as every item:
 * y up, +z toward the camera.
 */

import { type Color, Matrix4, Vector3 } from 'three';
import type { MeshBuilder } from '../../kit/MeshBuilder';

/** Translate to (x, y, z), then roll `a` about z. */
export const rotZ = (x: number, y: number, a: number, z = 0): Matrix4 =>
  new Matrix4().makeTranslation(x, y, z).multiply(new Matrix4().makeRotationZ(a));

/** Translate to (x, y, z), then pitch `a` about x (a cylinder turned by PI/2 points at the camera). */
export const rotX = (x: number, y: number, z: number, a: number): Matrix4 =>
  new Matrix4().makeTranslation(x, y, z).multiply(new Matrix4().makeRotationX(a));

/** A point on a circle of radius r about (cx, cy) in the xy plane, at angle a. */
export const arc = (cx: number, cy: number, r: number, a: number, z = 0): Vector3 =>
  new Vector3(cx + Math.cos(a) * r, cy + Math.sin(a) * r, z);

interface Tri {
  p: [Vector3, Vector3, Vector3];
  n: Vector3;
  color: Color;
  /** Cosine of the crease angle this face was added under. */
  cos: number;
}

const key = (v: Vector3): string => `${Math.round(v.x * 1e4)},${Math.round(v.y * 1e4)},${Math.round(v.z * 1e4)}`;

/**
 * Collects triangles, then writes them with per-corner normals averaged over the faces that meet
 * there within the crease angle: smooth where the surface is smooth, a hard edge where it folds.
 */
export class Smooth {
  private readonly tris: Tri[] = [];
  /** Faces meeting at more than this (radians) keep a hard edge; applies to faces added after it is set. */
  crease = 0.8;

  tri(a: Vector3, b: Vector3, c: Vector3, color: Color): this {
    const n = b.clone().sub(a).cross(c.clone().sub(a));
    if (n.lengthSq() < 1e-14) return this;
    this.tris.push({ p: [a.clone(), b.clone(), c.clone()], n, color, cos: Math.cos(this.crease) });
    return this;
  }

  /** Quad with corners counter-clockwise as seen from outside. */
  quad(a: Vector3, b: Vector3, c: Vector3, d: Vector3, color: Color): this {
    return this.tri(a, b, c, color).tri(a, c, d, color);
  }

  /** Write everything into `mb` (under `m` if given), then start empty. */
  flush(mb: MeshBuilder, m?: Matrix4): void {
    const at = new Map<string, Tri[]>();
    for (const t of this.tris) {
      for (const v of t.p) {
        const k = key(v);
        const list = at.get(k);
        if (list) list.push(t);
        else at.set(k, [t]);
      }
    }
    const write = (): void => {
      for (const t of this.tris) {
        const own = t.n.clone().normalize();
        const ns = t.p.map((v) => {
          const sum = new Vector3();
          for (const o of at.get(key(v)) ?? []) if (o.n.clone().normalize().dot(own) >= t.cos) sum.add(o.n);
          return sum.lengthSq() > 0 ? sum.normalize() : own;
        });
        const [a, b, c] = t.p;
        const [na, nb, nc] = ns;
        if (na && nb && nc) mb.triSmooth(a, b, c, na, nb, nc, t.color);
      }
    };
    if (m) mb.with(m, write);
    else write();
    this.tris.length = 0;
  }
}

/**
 * Rows of points joined into quads, row i to row i + 1 and point j to j + 1 (wrapping when
 * `closed`). Orientation: rows run along the surface, points round it counter-clockwise seen from
 * where the rows advance to, as a lathe's rings rising or a tube's rings along its path.
 */
export function grid(
  s: Smooth, rows: readonly Vector3[][], color: (i: number, j: number) => Color, closed = true,
): void {
  for (let i = 0; i + 1 < rows.length; i++) {
    const a = rows[i] ?? [];
    const b = rows[i + 1] ?? [];
    const n = closed ? a.length : a.length - 1;
    for (let j = 0; j < n; j++) {
      const j1 = (j + 1) % a.length;
      const a0 = a[j];
      const a1 = a[j1];
      const b0 = b[j];
      const b1 = b[j1];
      if (a0 && a1 && b0 && b1) s.quad(a0, a1, b1, b0, color(i, j));
    }
  }
}

/**
 * A surface of revolution about y. `profile` runs (radius, height) from the bottom round the
 * outside to the top; a radius of 0 closes it. `shape(angle, height)` scales the radius, for
 * folds and flutes. Colour per profile segment and side.
 */
export function lathe(
  s: Smooth,
  profile: ReadonlyArray<readonly [number, number]>,
  sides: number,
  color: (seg: number, side: number) => Color,
  shape?: (a: number, y: number) => number,
): void {
  const rows = profile.map(([r, y]) => {
    const ring: Vector3[] = [];
    for (let j = 0; j < sides; j++) {
      const a = (j / sides) * Math.PI * 2;
      const k = shape ? shape(a, y) : 1;
      ring.push(new Vector3(Math.sin(a) * r * k, y, Math.cos(a) * r * k));
    }
    return ring;
  });
  grid(s, rows, color, true);
}

/**
 * A tube along `path` with a radius per point (parallel-transported rings, so it never twists).
 * `closed` joins the ends into a loop (a ring, a handle); otherwise `caps` closes each end flat.
 */
export function tube(
  s: Smooth,
  path: readonly Vector3[],
  radii: readonly number[] | number,
  sides: number,
  color: Color | ((i: number) => Color),
  opts: { closed?: boolean; caps?: boolean } = {},
): void {
  const { closed = false, caps = true } = opts;
  const n = path.length;
  const pt = (i: number): Vector3 => path[closed ? (i + n) % n : Math.max(0, Math.min(n - 1, i))] ?? new Vector3();
  const tangents = path.map((_, i) => pt(i + 1).clone().sub(pt(i - 1)).normalize());
  const t0 = tangents[0] ?? new Vector3(1, 0, 0);
  const ref = Math.abs(t0.z) < 0.9 ? new Vector3(0, 0, 1) : new Vector3(0, 1, 0);
  let normal = t0.clone().cross(ref).normalize();
  const rows: Vector3[][] = [];
  path.forEach((p, i) => {
    const t = tangents[i] ?? t0;
    normal = normal.clone().addScaledVector(t, -normal.dot(t)).normalize();
    const bi = t.clone().cross(normal);
    const r = typeof radii === 'number' ? radii : radii[i] ?? 0;
    const ring: Vector3[] = [];
    for (let k = 0; k < sides; k++) {
      const a = (k / sides) * Math.PI * 2;
      ring.push(p.clone().addScaledVector(normal, Math.cos(a) * r).addScaledVector(bi, Math.sin(a) * r));
    }
    rows.push(ring);
  });
  if (closed && rows[0]) rows.push(rows[0]);
  const col = (i: number): Color => (typeof color === 'function' ? color(i) : color);
  grid(s, rows, (i) => col(i), true);
  if (closed || !caps) return;
  const first = rows[0] ?? [];
  const last = rows[n - 1] ?? [];
  for (let k = 0; k < sides; k++) {
    const k1 = (k + 1) % sides;
    const f0 = first[k];
    const f1 = first[k1];
    const l0 = last[k];
    const l1 = last[k1];
    if (f0 && f1) s.tri(pt(0), f1, f0, col(0));
    if (l0 && l1) s.tri(pt(n - 1), l0, l1, col(n - 1));
  }
}

/** A loop of radius `r` in the xy plane round (cx, cy, z), as a path for `tube`. */
export function circlePath(cx: number, cy: number, r: number, steps = 24, z = 0): Vector3[] {
  const out: Vector3[] = [];
  for (let i = 0; i < steps; i++) out.push(arc(cx, cy, r, (i / steps) * Math.PI * 2, z));
  return out;
}

/**
 * A polygon (xy, counter-clockwise seen from +z) extruded `depth` along z about `z`, its front edge
 * bevelled by `bevel`: a crisp flat badge (a cross, a bolt, a door leaf). A convex polygon's faces
 * are fanned; a concave one passes its own triangulation as index triples in `tris`.
 */
export function slab(
  s: Smooth,
  pts: ReadonlyArray<readonly [number, number]>,
  depth: number,
  color: Color,
  opts: { bevel?: number; bevelColor?: Color; sideColor?: Color; z?: number; tris?: readonly number[] } = {},
): void {
  const { bevel = 0, bevelColor = color, sideColor = color, z = 0 } = opts;
  const n = pts.length;
  const at = (i: number): readonly [number, number] => pts[(i + n) % n] ?? [0, 0];
  const inward = (i: number): Vector3 => {
    const [x0, y0] = at(i);
    const [x1, y1] = at(i + 1);
    return new Vector3(-(y1 - y0), x1 - x0, 0).normalize();
  };
  const outer = pts.map(([x, y]) => new Vector3(x, y, 0));
  const inner = outer.map((v, i) => {
    const n1 = inward(i - 1);
    const n2 = inward(i);
    return v.clone().addScaledVector(n1.clone().add(n2), bevel / (1 + n1.dot(n2)));
  });
  const zf = z + depth / 2;
  const zb = z - depth / 2;
  const zr = zf - bevel;
  const front = inner.map((v) => v.clone().setZ(zf));
  const rim = outer.map((v) => v.clone().setZ(bevel > 0 ? zr : zf));
  const back = outer.map((v) => v.clone().setZ(zb));
  const crease = s.crease;
  s.crease = 0.5;
  const fan: number[] = [];
  for (let i = 1; i + 1 < n; i++) fan.push(0, i, i + 1);
  const tris = opts.tris ?? fan;
  for (let t = 0; t + 2 < tris.length; t += 3) {
    const [i, j, k] = [tris[t] ?? 0, tris[t + 1] ?? 0, tris[t + 2] ?? 0];
    const [fi, fj, fk, bi, bj, bk] = [front[i], front[j], front[k], back[i], back[j], back[k]];
    if (fi && fj && fk) s.tri(fi, fj, fk, color);
    if (bi && bj && bk) s.tri(bi, bk, bj, sideColor);
  }
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    const [r0, r1, f0, f1, b0, b1] = [rim[i], rim[j], front[i], front[j], back[i], back[j]];
    if (!r0 || !r1 || !f0 || !f1 || !b0 || !b1) continue;
    if (bevel > 0) s.quad(r0, r1, f1, f0, bevelColor);
    s.quad(b0, b1, r1, r0, sideColor);
  }
  s.crease = crease;
}

/** A rectangle's corners for `slab`, centred on (cx, cy). */
export const rect = (cx: number, cy: number, w: number, h: number): Array<[number, number]> => [
  [cx - w / 2, cy - h / 2], [cx + w / 2, cy - h / 2], [cx + w / 2, cy + h / 2], [cx - w / 2, cy + h / 2],
];

/**
 * A closed outline (xy, clockwise seen from +z, star-shaped about `centre`) blown up into a
 * pillow `depth` thick front and back: a plump heart. Rows are the outline shrunk toward the
 * centre, each raised on a quarter circle, so the rim is round and the middle full.
 */
export function puffy(
  s: Smooth, outline: ReadonlyArray<readonly [number, number]>, centre: readonly [number, number], depth: number,
  color: Color, rows = 7,
): void {
  const [cx, cy] = centre;
  const ring = (k: number, sign: number): Vector3[] => {
    const th = (k / rows) * (Math.PI / 2);
    const sc = Math.cos(th);
    return outline.map(([x, y]) => new Vector3(cx + (x - cx) * sc, cy + (y - cy) * sc, sign * depth * Math.sin(th)));
  };
  const front: Vector3[][] = [];
  const back: Vector3[][] = [];
  for (let k = 0; k <= rows; k++) {
    front.push(ring(k, 1));
    back.push(ring(k, -1));
  }
  grid(s, front.reverse(), () => color);
  grid(s, back, () => color);
}
